import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';

interface AnimatedCounterProps {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}

/**
 * Formats a counter value for display.
 * @param value - Numeric value to format.
 * @param decimals - Decimal places when greater than zero.
 * @returns Formatted number string.
 */
const formatCounterValue = (value: number, decimals: number): string =>
  decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString();

/**
 * Animated count-up for hero KPI tiles; renders the final value immediately on print.
 * @param props - Target value and display formatting options.
 */
const AnimatedCounter = ({
  end,
  duration = 1.5,
  prefix = '',
  suffix = '',
  decimals = 0,
}: AnimatedCounterProps) => {
  const printStatic = useReportsPrintStaticLayout();
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  useEffect(() => {
    if (printStatic || !isInView) {
      return;
    }
    let startTime: number;
    const step = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
      }
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(eased * end);
      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };
    requestAnimationFrame(step);
  }, [printStatic, isInView, end, duration]);

  const displayValue = printStatic ? end : count;

  if (printStatic) {
    return (
      <span className="tabular-nums">
        {prefix}
        {formatCounterValue(displayValue, decimals)}
        {suffix}
      </span>
    );
  }

  return (
    <motion.span ref={ref} className="tabular-nums">
      {prefix}
      {formatCounterValue(displayValue, decimals)}
      {suffix}
    </motion.span>
  );
};

export default AnimatedCounter;
